import { Injectable } from '@angular/core';

import $ from 'jquery';
import * as _ from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { AttributeDataService } from './attribute-data.service';
import { TemplateEditorUtilsService } from './template-editor-utils.service';
import { TemplateEditorService } from './template-editor.service';

export type PlaylistComponentDirective = {
  type: string;
  element: Element;
  show: () => void;
  onBackHandler?: () => boolean | void;
  getLabel?: (componentId?: string) => string | undefined;
  usesBackTitleHeader?: () => boolean;
  backToMainView?: () => void;
  onAiHandler?: () => boolean | void;
  canShowAiButton?: (componentId?: string) => boolean;
};

export type ContextualToolbarCustomization = {
  left: string;
  top: string;
  width: string;
  height: string;
};

export type PlaylistItem = {
  id: string;
  index: number;
};

export type ContextualToolbarData = {
  id: string;
  component: any;
  customization: ContextualToolbarCustomization | null;
  playlistItem: PlaylistItem | null;
};

export type PlaylistComponentInfo = {
  type: string;
  id?: string;
  isLogo?: boolean;
  label?: string;
  layoutId?: string;
  directive?: PlaylistComponentDirective;
};

export type PlaylistComponentItem = {
  type: string;
  tagName?: string;
  title: string;
  subtitle?: string;
  icon?: string;
  panel?: string;
  visual?: boolean;
  userCanAdd?: boolean;
  playUntilDone?: boolean;
  requireRole?: string;
  requireFeature?: string;
  defaultAttributes?: { [key: string]: string | number | boolean | object };
  options?: { [key: string]: string | number | boolean };
  isMatIcon?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ComponentsService {
  static readonly COMPONENTS_MAP: { [key: string]: PlaylistComponentItem } = {
    'rise-branding-colors': {
      type: 'rise-branding-colors',
      icon: 'palette',
      panel: '.branding-colors-container',
      title: 'Color Settings',
    },
    'rise-branding': {
      type: 'rise-branding',
      icon: 'ratingStar',
      panel: '.branding-component-container',
      title: 'Brand',
    },
    'rise-override-brand-colors': {
      type: 'rise-override-brand-colors',
      icon: 'palette',
      title: 'Override Brand Colors',
    },
    'rise-data-counter': {
      type: 'rise-data-counter',
      icon: 'component-counter',
      isMatIcon: true,
      title: 'Counter',
    },
    'rise-data-financial': {
      type: 'rise-data-financial',
      icon: 'component-financial',
      isMatIcon: true,
      title: 'Financial',
    },
    'rise-data-table': {
      type: 'rise-data-table',
      icon: 'component-data-table',
      isMatIcon: true,
      title: 'Data Table',
    },
    'rise-text': {
      type: 'rise-text',
      icon: 'component-text',
      isMatIcon: true,
      title: 'Text',
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        fontsize: 100,
        multiline: true,
        verticalalign: 'middle',
        horizontalalign: 'center',
        textalign: 'center',
      },
    },
    'rise-icon': {
      type: 'rise-icon',
      icon: 'component-icon',
      isMatIcon: true,
      panel: '.icon-container',
      title: 'Icon',
      visual: false,
      userCanAdd: false,
      defaultAttributes: {
        type: 'fa4',
        name: 'television',
      },
    },
    'rise-image-logo': {
      type: 'rise-image-logo',
      icon: 'component-image',
      isMatIcon: true,
      panel: '.image-component-container',
      title: 'Logo Settings',
    },
    'rise-folder': {
      type: 'rise-folder',
      icon: 'component-folder',
      isMatIcon: true,
      panel: '.folder-component-container',
      title: 'Folder',
      subtitle: 'Images & Videos',
      playUntilDone: true,
      visual: true,
    },
    'rise-playlist': {
      type: 'rise-playlist',
      icon: 'component-playlist',
      isMatIcon: true,
      panel: '.rise-playlist-container',
      title: 'Playlist',
      playUntilDone: true,
      userCanAdd: true,
    },
    'rise-playlist-item': {
      type: 'rise-playlist-item',
      icon: 'presentation',
      isMatIcon: true,
      panel: '.playlist-item-container',
      title: 'Playlist Item',
    },
    'rise-playlist-item-selector': {
      type: 'rise-playlist-item-selector',
      title: 'Add Playlist Item',
    },
    'rise-layout-time': {
      type: 'rise-layout-time',
      tagName: 'rise-layout',
      icon: 'component-time',
      isMatIcon: true,
      title: 'Time',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'time-digital',
        },
        services: [
          {
            type: 'component',
            source: 'rise-time-date',
          },
        ],
      },
    },
    'rise-layout-date': {
      type: 'rise-layout-date',
      tagName: 'rise-layout',
      icon: 'component-date',
      isMatIcon: true,
      title: 'Date',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'date-inline',
        },
        services: [
          {
            type: 'component',
            source: 'rise-time-date',
          },
        ],
      },
    },
    'rise-web-page': {
      type: 'rise-web-page',
      icon: 'component-webpage',
      isMatIcon: true,
      title: 'Web Page',
      visual: true,
      userCanAdd: true,
    },
    'rise-html': {
      type: 'rise-html',
      icon: 'component-html',
      isMatIcon: true,
      title: 'HTML Embed',
      visual: true,
      userCanAdd: true,
    },
    'rise-video-streaming': {
      type: 'rise-video-streaming',
      icon: 'component-video-streaming',
      isMatIcon: true,
      title: 'Video Streaming',
      playUntilDone: true,
      visual: true,
      userCanAdd: true,
    },
    'rise-screen-sharing': {
      type: 'rise-screen-sharing',
      icon: 'component-screen-sharing',
      isMatIcon: true,
      title: 'Screen Share Code',
      subtitle: ' ',
      visual: true,
      userCanAdd: true,
      requireFeature: FeaturesService.FEATURE_SCREEN_SHARING,
    },
    'rise-stock-library': {
      type: 'rise-stock-library',
      icon: 'component-stock-library',
      isMatIcon: true,
      title: 'Stock Library',
      panel: '.stock-library-container',
      visual: true,
      userCanAdd: true,
      requireFeature: FeaturesService.FEATURE_ASSET_LIBRARY,
    },
    'rise-layout-calendar': {
      type: 'rise-layout-calendar',
      tagName: 'rise-layout',
      icon: 'component-calendar',
      isMatIcon: true,
      title: 'Calendar',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'calendar-scrolling',
        },
        services: [
          {
            type: 'component',
            source: 'rise-data-calendar',
          },
        ],
      },
    },
    'rise-layout-weather': {
      type: 'rise-layout-weather',
      tagName: 'rise-layout',
      icon: 'component-weather',
      isMatIcon: true,
      title: 'Weather',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'weather-current',
        },
        services: [
          {
            type: 'component',
            source: 'rise-data-weather',
          },
        ],
      },
    },
    'rise-layout-table': {
      type: 'rise-layout-table',
      tagName: 'rise-layout',
      icon: 'component-data-table',
      isMatIcon: true,
      title: 'List',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'table-card-grid',
        },
        services: [
          {
            type: 'component',
            source: 'rise-data-table',
          },
        ],
      },
    },
    'rise-layout-rss': {
      type: 'rise-layout-rss',
      tagName: 'rise-layout',
      icon: 'component-rss',
      isMatIcon: true,
      title: 'RSS',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'rss-cards',
        },
        services: [
          {
            type: 'component',
            source: 'rise-data-rss',
          },
        ],
      },
    },
    'rise-layout-counter': {
      type: 'rise-layout-counter',
      tagName: 'rise-layout',
      icon: 'component-counter',
      isMatIcon: true,
      title: 'Countdown',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'counter-squares',
        },
        services: [
          {
            type: 'component',
            source: 'rise-data-counter',
          },
        ],
      },
    },
    'rise-youtube': {
      type: 'rise-youtube',
      icon: 'component-youtube',
      isMatIcon: true,
      panel: '.youtube-component-container',
      title: 'YouTube',
      playUntilDone: true,
      visual: true,
      userCanAdd: true,
    },
    'rise-image': {
      type: 'rise-image',
      icon: 'component-image',
      isMatIcon: true,
      panel: '.image-component-container',
      title: 'Image',
      playUntilDone: true,
    },
    'rise-video': {
      type: 'rise-video',
      icon: 'component-video',
      isMatIcon: true,
      panel: '.video-component-container',
      title: 'Video',
      playUntilDone: true,
    },
    'rise-media-selector': {
      type: 'rise-media-selector',
      title: 'Select a Source',
    },
    'rise-image-generate-ai': {
      type: 'rise-image-generate-ai',
      title: 'Generate with AI',
    },
    'rise-layout-generate-ai': {
      type: 'rise-layout-generate-ai',
      title: 'Generate layout',
    },
    'rise-custom-font-selector': {
      icon: 'addFont',
      type: 'rise-custom-font-selector',
      title: 'Add Custom Font',
    },
    'rise-template-share-settings': {
      type: 'rise-template-share-settings',
      title: 'Save Presentation As Template',
    },
    'rise-presentation-selector': {
      type: 'rise-presentation-selector',
      icon: 'presentation',
      isMatIcon: true,
      panel: '.presentation-selector-container',
      title: 'Presentation',
      visual: true,
    },
    'rise-data-rss': {
      type: 'rise-data-rss',
      icon: 'component-rss',
      isMatIcon: true,
      title: 'RSS',
    },
    'rise-schedules': {
      type: 'rise-schedules',
      icon: 'schedule',
      title: 'Schedules',
    },
    'rise-slides': {
      type: 'rise-slides',
      icon: 'component-slides',
      isMatIcon: true,
      title: 'Google Slides',
      visual: true,
      userCanAdd: true,
      playUntilDone: true,
      defaultAttributes: {
        src: '',
      },
    },
    'rise-canva': {
      type: 'rise-canva',
      icon: 'component-canva',
      isMatIcon: true,
      title: 'Canva',
      visual: false,
      userCanAdd: false,
    },
    'rise-canva-connect': {
      type: 'rise-folder',
      icon: 'component-canva',
      isMatIcon: true,
      panel: '.canva-connect-container',
      title: 'Canva',
      visual: true,
      userCanAdd: true,
      options: {
        fileType: 'canva',
      },
    },
    'rise-powerbi': {
      type: 'rise-powerbi',
      icon: 'component-power-bi',
      isMatIcon: true,
      title: 'Power BI',
      visual: true,
      userCanAdd: true,
      requireFeature: FeaturesService.FEATURE_POWER_BI,
    },
    'rise-document': {
      type: 'rise-document',
      icon: 'component-document',
      isMatIcon: true,
      title: 'Document',
      playUntilDone: true,
    },
    'rise-domo': {
      type: 'rise-domo',
      icon: 'component-domo',
      isMatIcon: true,
      title: 'Domo',
      visual: true,
      userCanAdd: true,
      requireFeature: FeaturesService.FEATURE_DOMO,
    },
    'rise-storage-selector': {
      type: 'rise-storage-selector',
      icon: 'riseStorage',
      panel: '.storage-selector-container',
      title: 'Rise Storage',
    },
    'rise-time-date': {
      type: 'rise-time-date',
      icon: 'component-time',
      isMatIcon: true,
      title: 'Time and Date',
    },
    'rise-data-twitter': {
      type: 'rise-data-twitter',
      icon: 'twitter',
      title: 'Twitter',
    },
    'rise-data-weather': {
      type: 'rise-data-weather',
      icon: 'component-weather',
      isMatIcon: true,
      title: 'Weather',
    },
    'rise-data-calendar': {
      type: 'rise-data-calendar',
      icon: 'component-calendar',
      isMatIcon: true,
      title: 'Calendar',
    },
    'rise-social-media-posts': {
      type: 'rise-social-media-posts',
      icon: 'component-social-media-posts',
      isMatIcon: true,
      title: 'Social Media Posts',
      playUntilDone: true,
      visual: true,
      userCanAdd: true,
      requireFeature: FeaturesService.FEATURE_SOCIAL_MEDIA_POSTS,
    },
    'rise-social-media-followers': {
      type: 'rise-social-media-followers',
      icon: 'component-social-media-followers',
      isMatIcon: true,
      title: 'Social Media Followers',
      visual: true,
      userCanAdd: true,
      requireFeature: FeaturesService.FEATURE_SOCIAL_MEDIA_FOLLOWERS,
    },
    'rise-layout-ems': {
      type: 'rise-layout-ems',
      tagName: 'rise-layout',
      icon: 'component-ems',
      isMatIcon: true,
      title: 'Event Management',
      playUntilDone: false,
      visual: true,
      userCanAdd: true,
      requireFeature: FeaturesService.FEATURE_EMS,
      defaultAttributes: {
        layout: {
          type: 'file',
          layoutId: 'ems-room-schedule',
        },
        services: [
          {
            type: 'component',
            source: 'rise-data-calendar',
            attributes: {
              mode: 'ems',
            },
          },
        ],
      },
    },
  };

  static readonly LAYOUT_TYPE_CUSTOM = 'custom';

  static readonly LAYOUTS_MAP: { [key: string]: { [key: string]: string } } = {
    'rise-layout-weather': {
      CURRENT: 'weather-current',
      HORIZONTAL: 'weather-horizontal',
      VERTICAL: 'weather-vertical',
    },
    'rise-layout-time': {
      DIGITAL: 'time-digital',
    },
    'rise-layout-date': {
      INLINE: 'date-inline',
      STACKED: 'date-stacked',
    },
    'rise-layout-ems': {
      ROOM_SCHEDULE: 'ems-room-schedule',
      ROOM_STATUS: 'ems-room-status',
    },
    'rise-layout-calendar': {
      SCROLLING: 'calendar-scrolling',
      SINGLE: 'calendar-single',
    },
    'rise-layout-rss': {
      CARDS: 'rss-cards',
      SINGLE: 'rss-single',
      HEADLINE: 'rss-headline',
    },
    'rise-layout-counter': {
      SQUARES: 'counter-squares',
      CIRCLES: 'counter-circles',
      HORIZONTAL: 'counter-horizontal',
    },
    'rise-layout-table': {
      CARD_GRID: 'table-card-grid',
      SINGLE: 'table-single',
      TABLE: 'table-table',
    },
  };

  static readonly LAYOUT_DIRECTIVES_MAP: { [key: string]: string } = (() => {
    const map: { [key: string]: string } = {};
    _.forEach(ComponentsService.LAYOUTS_MAP, (layouts, componentType) => {
      _.forEach(layouts, (layoutId) => {
        map[layoutId] = componentType;
      });
    });
    return map;
  })();

  static readonly DocumentItem: PlaylistComponentItem = {
    type: 'rise-folder',
    title: 'Document',
    subtitle: 'pdf, docx, xlsx',
    icon: 'component-document',
    isMatIcon: true,
    options: {
      fileType: 'document',
    },
  };

  static readonly GooglePhotosItem: PlaylistComponentItem = {
    type: 'rise-folder',
    title: 'Google Photos',
    icon: 'googlePhotos',
    options: {
      fileType: 'google-photos-album',
    },
  };

  static readonly PowerpointItem: PlaylistComponentItem = {
    type: 'rise-document',
    title: 'PowerPoint',
    icon: 'component-power-point',
    isMatIcon: false,
    options: {
      fileType: 'powerpoint',
    },
  };

  static get COMPONENTS_ARRAY() {
    return _.values(ComponentsService.COMPONENTS_MAP);
  }

  static get PLAYLIST_COMPONENTS() {
    const list = ComponentsService.COMPONENTS_ARRAY.filter(
      (item) => item.visual,
    );
    const index = list.findIndex((item) => item.type === 'rise-folder');
    const documentItem = _.cloneDeep(ComponentsService.DocumentItem);
    const powerpointItem = _.cloneDeep(ComponentsService.PowerpointItem);
    list.splice(index + 1, 0, documentItem);
    list.splice(index + 2, 0, powerpointItem);

    return list;
  }

  static get USER_COMPONENTS_ARRAY() {
    const list = ComponentsService.COMPONENTS_ARRAY.filter(
      (item) => item.userCanAdd,
    );

    const index = list.findIndex((item) => item.type === 'rise-text');
    list.splice(
      index + 1,
      0,
      {
        type: 'rise-folder',
        icon: 'component-image',
        isMatIcon: true,
        title: 'Image',
        options: {
          fileType: 'image',
          singleFile: true,
        },
      },
      {
        type: 'rise-folder',
        icon: 'component-video',
        isMatIcon: true,
        title: 'Video',
        options: {
          fileType: 'video',
          singleFile: true,
        },
      },
      {
        type: 'rise-folder',
        icon: 'component-folder',
        isMatIcon: true,
        panel: '.folder-component-container',
        title: 'Folder',
        subtitle: 'Images & Videos',
        playUntilDone: true,
        options: {
          fileType: 'folder',
          singleFile: true,
        },
      },
    );

    const index2 = list.findIndex((item) => item.type === 'rise-domo');
    let documentItem = _.cloneDeep(ComponentsService.DocumentItem);
    let powerpointItem = _.cloneDeep(ComponentsService.PowerpointItem);

    documentItem.options.singleFile = true;

    list.splice(index2 + 1, 0, documentItem);
    list.splice(index2 + 2, 0, powerpointItem);

    return list;
  }

  static getLayoutSelection(
    componentLayout: any,
    defaultLayoutId: string,
  ): string {
    if (componentLayout?.type === ComponentsService.LAYOUT_TYPE_CUSTOM) {
      return ComponentsService.LAYOUT_TYPE_CUSTOM;
    }
    return componentLayout?.layoutId || defaultLayoutId;
  }

  static applyLayoutSelection(componentLayout: any, selection: string): any {
    if (selection === ComponentsService.LAYOUT_TYPE_CUSTOM) {
      // custom selected: keep type/layoutHtml/baseComponent, never write layoutId
      return componentLayout || {};
    }
    const layout =
      componentLayout?.type === ComponentsService.LAYOUT_TYPE_CUSTOM
        ? // leaving custom: fresh object so layoutHtml/baseComponent don't linger
          { type: 'file' }
        : componentLayout || {};
    layout.layoutId = selection;
    return layout;
  }

  static buildCustomLayout(
    layoutHtml: string,
    baseComponent: string,
    usedAttributes?: string[],
  ): any {
    return {
      type: ComponentsService.LAYOUT_TYPE_CUSTOM,
      layoutHtml,
      baseComponent,
      ...(usedAttributes ? { usedAttributes } : {}),
    };
  }

  // Custom layouts list the attributes they use; stock layouts and custom
  // layouts without a list use every attribute.
  static usesLayoutAttribute(componentLayout: any, attribute: string): boolean {
    return (
      componentLayout?.type !== ComponentsService.LAYOUT_TYPE_CUSTOM ||
      !Array.isArray(componentLayout.usedAttributes) ||
      componentLayout.usedAttributes.includes(attribute)
    );
  }

  selected = null;
  showAttributeList = true;
  directives = {};
  pages = [];

  panelIcon;
  panelTitle;

  private updateComponentSource = new Subject<string>();
  componentValueUpdated$ = this.updateComponentSource.asObservable();

  private contextualToolbarSource =
    new BehaviorSubject<ContextualToolbarData | null>(null);
  contextualToolbar$ = this.contextualToolbarSource.asObservable();

  constructor(
    private templateEditorUtils: TemplateEditorUtilsService,
    private attributeDataService: AttributeDataService,
  ) {
    this.reset();
  }

  reset() {
    this.selected = null;
    this.showAttributeList = true;
    this.directives = {};
    this.pages = [];
  }

  updateComponentValue(value: string) {
    this.updateComponentSource.next(value);
  }

  registerDirective(directive) {
    if (!directive.element || !directive.element.jquery) {
      directive.element = $(directive.element);
    }

    directive.element.hide();
    this.directives[directive.type] = directive;

    _.defaults(directive, ComponentsService.COMPONENTS_MAP[directive.type], {
      panel: '.attribute-editor-component',
    });

    if (directive.onPresentationOpen) {
      directive.onPresentationOpen();
    }
  }

  _getFolderType(component: any): string | null {
    if (component?.id) {
      return this.attributeDataService.getAvailableAttributeData(
        component.id,
        'type',
      );
    } else if (component?.attributes?.type) {
      return component.attributes.type;
    } else if (component?.options?.fileType) {
      return component.options.fileType;
    }
    return null;
  }

  _getComponentMapEntry(component: any): PlaylistComponentItem | null {
    const componentType = component?.type || component?.tagName;

    if (
      componentType === 'rise-folder' ||
      component?.tagName === 'rise-folder'
    ) {
      const folderType = this._getFolderType(component);
      if (folderType === 'canva') {
        return ComponentsService.COMPONENTS_MAP['rise-canva-connect'] || null;
      }
    }

    return componentType
      ? ComponentsService.COMPONENTS_MAP[componentType] || null
      : null;
  }

  _getDirective(component) {
    if (!component) {
      return null;
    } else if (component.directive) {
      return component.directive;
    } else if (component.type === 'rise-layout') {
      return this._getLayoutDirective(component);
    } else if (
      component.type === 'rise-folder' ||
      component.tagName === 'rise-folder'
    ) {
      const type = this._getFolderType(component);
      if (type === 'canva') {
        return this.directives['rise-canva-connect'] || null;
      }
      return this.directives['rise-folder'] || null;
    } else {
      return (
        this.directives[component.type] ||
        this.directives[component.tagName] ||
        null
      );
    }
  }

  _getLayoutDirective(component) {
    // layoutId in Playlist Items or User Components are in component.layoutId
    let layoutId = component.layoutId;

    if (!layoutId) {
      // fetch fresh layoutId from attribute data because it might have been changed
      // and not be the same as in the blueprint
      const layoutData = this.attributeDataService.getAvailableAttributeData(
        component.id,
        'layout',
      );
      const layoutObj = this.templateEditorUtils.safeParseJson(layoutData, {});

      if (layoutObj?.type === ComponentsService.LAYOUT_TYPE_CUSTOM) {
        return this.directives[layoutObj.baseComponent] || null;
      }

      layoutId = layoutObj?.layoutId;
    }

    if (!layoutId) {
      return null;
    }

    const componentType = ComponentsService.LAYOUT_DIRECTIVES_MAP[layoutId];
    return componentType ? this.directives[componentType] : null;
  }

  getSelectedDirective() {
    var component = this.selected;

    return this._getDirective(component);
  }

  editComponent(component?, options?) {
    if (component?.isLogo === true) {
      component = {
        id: component.id,
        type: 'rise-image-logo',
        layoutId: component.layout?.layoutId,
      };
    }

    const directive = this._getDirective(component);
    const currentPage = this.getCurrentPage();
    const isSamePage =
      component && this._isSameComponentPage(currentPage, component);

    this.selected = component;

    if (isSamePage) {
      this._showElement(component, 'right');
    } else {
      this.showNextPage(component);
    }

    if (directive && directive.show) {
      directive.show(options);
    }

    this._showAttributeList(false);

    if (options?.isPlaylistItem) {
      this.highlightComponent(null);
      setTimeout(() => {
        this.editComponentInPreview(component?.id || null);
      }, 100);
    } else {
      this.highlightComponent(component?.id || null);
    }
  }

  onBackButton() {
    var directive = this.getSelectedDirective();

    const shouldSkipHighlight = directive?.usesBackTitleHeader?.() ?? false;
    if (!shouldSkipHighlight) {
      this.highlightComponent(null);
      this.editComponentInPreview(null);
    }

    if (!directive || !directive.onBackHandler || !directive.onBackHandler()) {
      this.showPreviousPage();
    }

    if (this.selected?.id && !shouldSkipHighlight) {
      this.highlightComponent(this.selected.id);
    }
  }

  // Private
  backToList() {
    var directive = this.getSelectedDirective();

    if (directive && directive.element) {
      directive.element.hide();
    }

    this.resetPanelHeader();

    this.selected = null;
    this.pages = [];

    this._showAttributeList(true);
  }

  getComponentIcon(component?) {
    var directive = this._getDirective(component);

    if (directive && directive.getIcon) {
      return directive.getIcon(component.id) || directive.icon;
    } else if (directive && directive.icon) {
      return directive.icon;
    } else if (component) {
      const componentType = component.type || component.tagName;
      if (
        componentType &&
        ComponentsService.COMPONENTS_MAP[componentType]?.icon
      ) {
        return ComponentsService.COMPONENTS_MAP[componentType].icon;
      }
    }

    return 'blank';
  }

  getComponentTitle(component?) {
    var directive = this._getDirective(component);

    if (directive?.getLabel) {
      const customLabel = directive.getLabel(component?.id);

      if (customLabel) {
        return customLabel;
      }
    }

    if (this.panelTitle) {
      return this.panelTitle;
    }

    if (component?.label) {
      return component.label;
    }

    if (directive?.title) {
      return directive.title;
    }

    return '';
  }

  getComponentName(component) {
    var directive = this._getDirective(component);
    if (component && component.label) {
      return component.label;
    } else if (directive && directive.getName) {
      return directive.getName(component.id) || directive.title;
    } else if (directive) {
      return directive.title;
    } else {
      return '';
    }
  }

  isMatIcon(component: any): boolean {
    const directive = this._getDirective(component);
    if (directive?.isMatIcon === true) {
      return true;
    }

    if (component?.isMatIcon === true) {
      return true;
    }

    const componentMap = this._getComponentMapEntry(component);

    return componentMap?.isMatIcon === true;
  }

  postMessageToTemplate(message) {
    let iframe: HTMLIFrameElement | null = null;
    iframe = window.document.querySelector('iframe.presentation-preview');

    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        JSON.stringify(message),
        TemplateEditorService.HTML_TEMPLATE_DOMAIN,
      );
    }
  }

  highlightComponent(componentId) {
    var message = {
      type: 'highlightComponent',
      value: componentId,
    };
    this.postMessageToTemplate(message);
  }

  editComponentInPreview(componentId: string) {
    var message = {
      type: 'editComponentInPreview',
      value: componentId,
    };
    this.postMessageToTemplate(message);
  }

  refreshComponentDataInPreview(componentId: string) {
    var message = {
      type: 'refreshComponentDataInPreview',
      value: componentId,
    };
    this.postMessageToTemplate(message);
  }

  isHeaderBottomRuleVisible(component) {
    var directive = this._getDirective(component);

    return directive && directive.isHeaderBottomRuleVisible
      ? directive.isHeaderBottomRuleVisible()
      : true;
  }

  isBackTitleHeaderVisible(component) {
    const directive = this._getDirective(component);

    return directive?.usesBackTitleHeader?.() ?? false;
  }

  getCurrentPage() {
    return this.pages.length > 0 ? this.pages[this.pages.length - 1] : null;
  }

  getCurrentPagePlaylistItem(): PlaylistItem | null {
    const page = this.getCurrentPage();
    if (!page) {
      return null;
    }
    return this.parsePlaylistItemId(page.id);
  }

  showNextPage(newPage) {
    var currentPage = this.getCurrentPage();

    this.pages.push(newPage);
    this._swapToLeft(currentPage, newPage);
  }

  showPreviousPage() {
    var currentPage = this.pages.length > 0 ? this.pages.pop() : null;
    var previousPage = this.getCurrentPage();

    if (!previousPage) {
      this.backToList();
    } else {
      this.selected = previousPage;

      this._swapToRight(currentPage, previousPage);
    }
  }

  removePageFromStack(type: string): boolean {
    const pageIndex = this.pages
      .map((page) => page?.type || page?.tagName)
      .lastIndexOf(type);

    if (pageIndex === -1) {
      return false;
    }

    this.pages.splice(pageIndex, 1);
    return true;
  }

  resetPanelHeader() {
    this.setPanelIcon(null);
    this.setPanelTitle(null);
  }

  setPanelIcon(panelIcon) {
    this.panelIcon = panelIcon;
  }

  setPanelTitle(panelTitle) {
    this.panelTitle = panelTitle;
  }

  editHighlightedComponent(componentId: string) {
    if (!componentId) {
      this.onBackButton();
      return;
    }

    let component = this.attributeDataService.getComponent(componentId);

    if (component) {
      if (this.selected) {
        this.backToList();
      }

      if (component.userComponent) {
        component = {
          id: component.id,
          type: component.tagName,
          ...(component.isLogo === true ? { isLogo: true } : {}),
          layoutId: component.layout?.layoutId,
        };
      }

      this.editComponent(component);
    }
  }

  showContextualToolbar(data: {
    id: string | null;
    customization?: ContextualToolbarCustomization | null;
    playlistItem?: PlaylistItem | null;
  }) {
    if (!data || !data.id) {
      this.contextualToolbarSource.next(null);
      return;
    }

    const component = this.attributeDataService.getComponent(data.id);
    if (!component) {
      this.contextualToolbarSource.next(null);
      return;
    }

    this.contextualToolbarSource.next({
      id: data.id,
      component,
      customization: data.customization,
      playlistItem: data.playlistItem,
    });
  }

  _showAttributeList(value) {
    this.showAttributeList = value;
  }

  _removeAnimationClasses(element) {
    element.removeClass('attribute-editor-show-from-right');
    element.removeClass('attribute-editor-show-from-left');
  }

  _showElement(component, direction, delay?) {
    var directive = this._getDirective(component);
    var element =
      directive &&
      directive.panel &&
      this.templateEditorUtils.findElement(directive.panel, directive.element);

    if (directive && directive.element) {
      directive.element.show();
    }

    if (!element) {
      return;
    }

    this._removeAnimationClasses(element);
    element.addClass('attribute-editor-show-from-' + direction);

    setTimeout(() => {
      element.show();
    }, delay || 0);
  }

  _hideElement(component, delay?) {
    if (
      component &&
      this.selected &&
      this._isSameComponentPage(component, this.selected)
    ) {
      return;
    }

    var directive = this._getDirective(component);
    var selectedDirective = this.getSelectedDirective();

    var element =
      directive &&
      directive.panel &&
      this.templateEditorUtils.findElement(directive.panel, directive.element);

    if (
      directive &&
      directive.element &&
      !directive.element.is(selectedDirective.element)
    ) {
      directive.element.hide();
    }

    if (!element) {
      return;
    }

    setTimeout(() => {
      element.hide();
    }, delay || 0);
  }

  _isSameComponentPage(currentPage, nextPage) {
    if (!currentPage || !nextPage) {
      return false;
    }

    const currentType = currentPage.type || currentPage.tagName;
    const nextType = nextPage.type || nextPage.tagName;

    if (currentType !== nextType) {
      return false;
    }

    if (currentPage.id && nextPage.id) {
      return currentPage.id === nextPage.id;
    }

    const currentDirectiveType = this._getDirective(currentPage)?.type;
    const nextDirectiveType = this._getDirective(nextPage)?.type;

    if (currentDirectiveType && nextDirectiveType) {
      if (currentDirectiveType !== nextDirectiveType) {
        return false;
      }
    }

    if (
      currentType === 'rise-folder' ||
      currentPage.tagName === 'rise-folder' ||
      nextPage.tagName === 'rise-folder'
    ) {
      return this._getFolderType(currentPage) === this._getFolderType(nextPage);
    }

    return true;
  }

  _swapToLeft(swappedOutSelector, swappedInSelector) {
    this._showElement(swappedInSelector, 'right');
    this._hideElement(swappedOutSelector);
  }

  _swapToRight(swappedOutSelector, swappedInSelector) {
    this._showElement(swappedInSelector, 'left');
    this._hideElement(swappedOutSelector);
  }

  parsePlaylistItemId(id: string): PlaylistItem | null {
    const match = id?.match(/^(\S+) (\d+)$/);
    if (!match) {
      return null;
    }

    return {
      id: match[1],
      index: parseInt(match[2], 10),
    };
  }
}
